import type { FC, SetStateAction } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldTextArea } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { patchPatientConsultationMotiveCommentSchema } from "@/schemas/patient";
import type { Patient } from "@/types/api/patient";
import { resetForm } from "@/utils/form";

interface PatchPatientConsultationMotiveCommentModalProps {
  patient: Patient;
  isOpen: boolean;
  setIsOpen: React.Dispatch<SetStateAction<boolean>>;
}

const PatchPatientConsultationMotiveCommentModal: FC<
  PatchPatientConsultationMotiveCommentModalProps
> = ({ patient, isOpen, setIsOpen }) => {
  const { mutate } = usePatchPatient(patient.id);

  const { consultationMotiveComment } = patient;

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      consultationMotiveComment: consultationMotiveComment ?? null,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchPatientConsultationMotiveCommentSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setIsOpen(false);
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      open={isOpen}
      onOpenChange={() => setIsOpen(false)}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldTextArea
          Field={Field}
          schema={patchPatientConsultationMotiveCommentSchema}
          name={"consultationMotiveComment"}
          placeholder="Écrire..."
        />
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <DialogFooter className="flex gap-3 px-6">
        <Button
          type="button"
          className="w-full"
          onClick={() => {
            setIsOpen(false);
          }}
          variant={"outline"}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          className="w-full"
          variant={"blue"}
          onClick={() => {
            handleSubmit();
          }}
        >
          Générer
        </Button>
      </DialogFooter>
    </DialogCustom>
  );
};

export default PatchPatientConsultationMotiveCommentModal;
